import { createTheme } from '@mui/material/styles';

const sharedThemeConfig = {
	typography: {
		fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		fontSize: 13,
		h1: { fontSize: '17px', fontWeight: 600 },
		h2: { fontSize: '14px', fontWeight: 600 },
		h3: { fontSize: '13px', fontWeight: 600 },
		body1: { fontSize: '13px', lineHeight: 1.45 },
		body2: { fontSize: '12px', lineHeight: 1.45 },
		caption: { fontSize: '11px', lineHeight: 1.4 },
		overline: {
			fontSize: '10px',
			fontWeight: 600,
			letterSpacing: '0.07em',
			textTransform: 'uppercase',
		},
		button: { textTransform: 'none', fontWeight: 500, fontSize: '12px' },
	},
	shape: {
		borderRadius: 8,
	},
	spacing: 4,
	layout: {
		navHeight: 52,
		sidebarWidth: 200,
	},
};

const irWorkstationTheme = createTheme({
	...sharedThemeConfig,
	palette: {
		primary: {
			main: '#378add',
		},
		background: {
			default: '#f1f0ec',
			paper: '#ffffff',
			subtle: '#f7f7f5',
		},
		text: {
			primary: '#1a1a18',
			secondary: '#6b6b67',
			tertiary: '#a8a8a4',
		},
		divider: 'rgba(0,0,0,0.1)',
		border: {
			light: 'rgba(0,0,0,0.1)',
			main: 'rgba(0,0,0,0.18)',
		},
		status: {
			blue: {
				bg: '#e6f1fb',
				text: '#0c447c',
				border: '#b5d4f4',
				hover: '#d4e8f8',
				icon: '#185fa5',
			},
			green: { bg: '#eaf3de', text: '#27500a' },
			amber: { bg: '#faeeda', text: '#633806' },
			red: { bg: '#fcebeb', text: '#791f1f' },
			teal: { bg: '#e1f5ee', text: '#085041' },
			gray: { bg: '#f1efe8', text: '#444441' },
		},
		vdr: {
			bg: '#f0ecff',
			text: '#4c1d95',
			border: '#c4b5fd',
			accent: '#7c3aed',
			hover: '#e4daff',
		},
		row: {
			featured: { bg: '#f5f9ff', hover: '#edf5ff' },
			vdrSourced: { bg: '#faf8ff' },
			vdrFeatured: { bg: '#f5f0ff', hover: '#ede6ff' },
		},
		signal: {
			red: '#e24b4a',
			amber: '#ef9f27',
			green: '#1d9e75',
			gray: '#b4b2a9',
			blue: '#0288d1',
		},
		score: {
			down: '#a32d2d',
			up: '#27500a',
			warn: '#854f0b',
		},
		commitmentByStage: {
			initialContact: '#9CA3AF',
			ppmSent: '#60A5FA',
			inDiscussions: '#2DD4BF',
			waiting: '#FBBF24',
			lost: '#F87171',
			commitmentWon: '#4ADE80',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'*, *::before, *::after': { boxSizing: 'border-box' },
				body: { margin: 0, padding: 0 },
				'::-webkit-scrollbar': { width: '4px' },
				'::-webkit-scrollbar-track': { background: 'transparent' },
				'::-webkit-scrollbar-thumb': {
					background: 'rgba(0,0,0,0.18)',
					borderRadius: '2px',
				},
			},
		},
		MuiAppBar: {
			defaultProps: { elevation: 0 },
			styleOverrides: {
				root: {
					backgroundColor: '#ffffff',
					color: '#1a1a18',
					borderBottom: '0.5px solid rgba(0,0,0,0.18)',
					borderRadius: 0,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 500,
					fontSize: '12px',
					borderRadius: 6,
					padding: '5px 12px',
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					borderLeft: '0.5px solid rgba(0,0,0,0.18)',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 12,
				},
			},
		},
	},
});

export const irWorkstationDarkTheme = createTheme({
	...sharedThemeConfig,
	palette: {
		mode: 'dark',
		primary: {
			main: '#8C8EFF',
			light: '#B0B1FF',
			dark: '#6B6DCF',
			contrastText: '#FFFFFF',
		},
		background: {
			default: '#181A24',
			paper: '#23243A',
			subtle: '#25253A',
		},
		text: {
			primary: '#F3F6F9',
			secondary: '#B0B3C6',
			tertiary: '#767792',
		},
		divider: 'rgba(255, 255, 255, 0.12)',
		border: {
			light: 'rgba(255, 255, 255, 0.1)',
			main: 'rgba(255, 255, 255, 0.18)',
		},
		status: {
			blue: {
				bg: 'rgba(55, 138, 221, 0.2)',
				text: '#7ebbff',
				border: 'rgba(55, 138, 221, 0.4)',
				hover: 'rgba(55, 138, 221, 0.3)',
				icon: '#7ebbff',
			},
			green: { bg: 'rgba(74, 222, 128, 0.15)', text: '#4ade80' },
			amber: { bg: 'rgba(251, 191, 36, 0.15)', text: '#fbbf24' },
			red: { bg: 'rgba(248, 113, 113, 0.15)', text: '#f87171' },
			teal: { bg: 'rgba(45, 212, 191, 0.15)', text: '#2dd4bf' },
			gray: { bg: 'rgba(255, 255, 255, 0.08)', text: '#B0B3C6' },
		},
		vdr: {
			bg: 'rgba(124, 58, 237, 0.2)',
			text: '#c4b5fd',
			border: 'rgba(124, 58, 237, 0.4)',
			accent: '#a78bfa',
			hover: 'rgba(124, 58, 237, 0.3)',
		},
		row: {
			featured: {
				bg: 'rgba(55, 138, 221, 0.12)',
				hover: 'rgba(55, 138, 221, 0.18)',
			},
			vdrSourced: { bg: 'rgba(124, 58, 237, 0.1)' },
			vdrFeatured: {
				bg: 'rgba(124, 58, 237, 0.15)',
				hover: 'rgba(124, 58, 237, 0.22)',
			},
		},
		signal: {
			red: '#f87171',
			amber: '#fbbf24',
			green: '#34d399',
			gray: '#767792',
			blue: '#60a5fa',
		},
		score: {
			down: '#f87171',
			up: '#4ade80',
			warn: '#fbbf24',
		},
		commitmentByStage: {
			initialContact: '#9CA3AF',
			ppmSent: '#60A5FA',
			inDiscussions: '#2DD4BF',
			waiting: '#FBBF24',
			lost: '#F87171',
			commitmentWon: '#4ADE80',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'*, *::before, *::after': { boxSizing: 'border-box' },
				body: { margin: 0, padding: 0 },
				'::-webkit-scrollbar': { width: '4px' },
				'::-webkit-scrollbar-track': { background: 'transparent' },
				'::-webkit-scrollbar-thumb': {
					background: 'rgba(255, 255, 255, 0.2)',
					borderRadius: '2px',
				},
			},
		},
		MuiAppBar: {
			defaultProps: { elevation: 0 },
			styleOverrides: {
				root: {
					backgroundColor: '#23243A',
					color: '#F3F6F9',
					borderBottom: '0.5px solid rgba(255, 255, 255, 0.12)',
					borderRadius: 0,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 500,
					fontSize: '12px',
					borderRadius: 6,
					padding: '5px 12px',
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					borderLeft: '0.5px solid rgba(255, 255, 255, 0.12)',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 12,
				},
			},
		},
	},
});

export default irWorkstationTheme;
